import type { RibbonFlyoutAnchorProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon';
import {
    RibbonControlGroupDefinitionMLR,
    RibbonControlGroupDefinitionSLR,
} from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/components';
import type { Layout } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/components/RibbonControlGroupMLR';
import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computedFn } from 'mobx-utils/lib/computedFn';
import type { ClientItemId } from 'owa-client-ids';
import { getIsViewModeSelected } from 'owa-command-ribbon-store';
import { getFocusIsOnLoopForViewStateId } from 'owa-fluid-integrations/lib/selectors/getFocusIsOnLoopForViewStateId';
import loc from 'owa-localize';
import { getMainWindowComposeViewState } from 'owa-mail-compose-actions/lib/selectors/getMainWindowComposeViewState';
import type { ComposeViewState } from 'owa-mail-compose-store';
import isCLPEnabled from 'owa-mail-protection/lib/utils/clp/isCLPEnabled';
import type { MailRibbonGroup } from 'owa-mail-ribbon-store-shared-types';
import type { RibbonGroupId, RibbonControlId } from 'owa-ribbon-ids';
import { MailRibbonTabId } from 'owa-ribbon-ids';
import type { RibbonControlDefinitionProps } from 'owa-mail-ribbon-utils';
import {
    defaultGroupStringMap,
    MutableControlProps,
    shouldShowRibbonGroup,
} from 'owa-mail-ribbon-utils';
import { isEncryptionAvailable } from 'owa-mail-ribbon-utils/lib/isEncryptionAvailable';
import { isConsumer } from 'owa-session-store';
import { CommandingViewMode } from 'owa-outlook-service-option-store/lib/store/schema/options/CommandingOptions';
import {
    createRibbonGroupMLR,
    createRibbonGroupSLR,
    createRibbonGroupToolbar,
} from './createRibbonGroup';
import { getControlsToDisplay } from './getControlsToDisplay';

/**
 * Retrieves the SLR groups within a given tab, as well as the actions that fall under each group.
 * @param tabId the MailRibbonTabId for the tab that is calling this function
 * @param groups the given tab's configuration as is saved in that tab's store
 * @param getControlDefinition function that returns the control definition given a controlId
 * @param props properties for ribbonIdToControlMap
 * @returns an object that contains the sub-groups and corresponding actions of the given tab
 */
export const getTabGroups = computedFn(
    (
        tabId: MailRibbonTabId,
        groups: MailRibbonGroup[],
        getControlDefinition: (
            controlId: RibbonControlId,
            props: RibbonControlDefinitionProps
        ) => RibbonControlProps | undefined,
        props: RibbonControlDefinitionProps
    ): RibbonControlGroupDefinitionSLR[] => {
        let tabGroups: RibbonControlGroupDefinitionSLR[] = new Array();
        const mutableProps: MutableControlProps = {
            isClpEnabled: isCLPEnabled(),
            isEncryptionAvailable: isEncryptionAvailable(),
            isConsumerAcct: isConsumer(),
        };

        for (const group of groups) {
            if (!shouldShowRibbonGroup(tabId, group.groupId, mutableProps, props)) {
                continue;
            }

            let groupDefinition: RibbonControlGroupDefinitionSLR = getIsViewModeSelected(
                CommandingViewMode.CommandBar
            )
                ? createRibbonGroupToolbar(tabId, group.groupId, props.popoutComposeViewState)
                : createRibbonGroupSLR(tabId, group.groupId, props.popoutComposeViewState);

            groupDefinition.title = getGroupTitle(group);
            // Get button objects that fall under the current group (stored in group)
            groupDefinition.controls = getControlsToDisplay(
                tabId,
                group.controlIds,
                getControlDefinition,
                mutableProps,
                props
            );
            if (tabId === MailRibbonTabId.Tab_Format) {
                groupDefinition.fastDisabled = getFocusIsOnLoopForViewStateId(
                    getMainWindowComposeViewState()?.composeId
                );
            }
            tabGroups.push(groupDefinition);
        }

        return tabGroups;
    }
);

/**
 * Retrieves the MLR groups within a given tab, as well as the actions that fall under each group.
 * @param tabId the MailRibbonTabId for the tab that is calling this function
 * @param groups the given tab's configuration as is saved in that tab's store
 * @param getControlDefinition function that returns the control definition given a controlId
 * @param getGroupLayouts function that returns the layout(s) given a controlId, or undefined, if none
 * @param getCommonGroupFlyout function that returns the "collapse" flyoutAnchor definition for a controlId, or undefined, if none
 * @param props properties for ribbonIdToControlMap
 * @returns an object that contains the sub-groups and corresponding actions of the given tab
 */
export const getMLRTabGroups = computedFn(
    (
        tabId: MailRibbonTabId,
        groups: MailRibbonGroup[],
        getControlDefinition: (
            controlId: RibbonControlId,
            props: RibbonControlDefinitionProps
        ) => RibbonControlProps | undefined,
        getGroupLayouts: (
            groupId: RibbonGroupId,
            popoutComposeViewState?: ComposeViewState,
            itemId?: ClientItemId
        ) => Layout[] | undefined,
        getCommonGroupFlyout: (
            groupId: RibbonGroupId,
            popoutComposeViewState?: ComposeViewState
        ) => RibbonFlyoutAnchorProps | undefined,
        props: RibbonControlDefinitionProps
    ): RibbonControlGroupDefinitionMLR[] => {
        let tabGroups: RibbonControlGroupDefinitionMLR[] = new Array();
        const mutableProps: MutableControlProps = {
            isClpEnabled: isCLPEnabled(),
            isEncryptionAvailable: isEncryptionAvailable(),
            isConsumerAcct: isConsumer(),
        };

        for (const group of groups) {
            if (!shouldShowRibbonGroup(tabId, group.groupId, mutableProps, props)) {
                continue;
            }

            // Get button objects that fall under the current group (stored in group)
            const controls: RibbonControlProps[] = getControlsToDisplay(
                tabId,
                group.controlIds,
                getControlDefinition,
                mutableProps,
                props
            );

            /**
             * Some groups only contain buttons which conditionally appear, whether because of type of account (Enterprise vs. Consumer) or
             * functionality that the admin may opt in/out of. In these cases, we want to hide the MLR group.
             *
             * Note: in SLR, this is only an issue for the customizer, which will show empty groups in these cases. We don't have a great solution
             * for this yet, since Multi-Account support is still being developed, and will impact the solution.
             */
            if (controls.length === 0) {
                continue;
            }

            const groupDefinition: RibbonControlGroupDefinitionMLR = createRibbonGroupMLR(
                tabId,
                group.groupId,
                props.popoutComposeViewState
            );

            groupDefinition.title = getGroupTitle(group);
            groupDefinition.controls = controls;

            if (tabId === MailRibbonTabId.Tab_Format) {
                groupDefinition.fastDisabled = getFocusIsOnLoopForViewStateId(
                    getMainWindowComposeViewState()?.composeId
                );
            }

            // Get layouts (if any)
            groupDefinition.layouts = getGroupLayouts(
                group.groupId,
                props.popoutComposeViewState,
                props.itemId
            );

            // Get collapse Flyout info (if any)
            groupDefinition.commonGroupFlyout = getCommonGroupFlyout(
                group.groupId,
                props.popoutComposeViewState
            );

            tabGroups.push(groupDefinition);
        }

        return tabGroups;
    }
);

function getGroupTitle(group: MailRibbonGroup): string {
    // If groupName isn't undefined, that means user has changed the name away from the default group name.
    if (group.groupName != undefined) {
        return group.groupName;
    }

    if (defaultGroupStringMap.has(group.groupId)) {
        return loc(defaultGroupStringMap.get(group.groupId)!);
    }

    throw new Error('Group ' + group.groupId + ' was not added to defaultGroupStringMap.ts');
}
